import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  View,
  Text,
  StyleSheet,
  ScrollView,
  TextInput,
  TouchableOpacity,
  ActivityIndicator,
  Platform,
  FlatList,
  Modal as RNModal,
  Switch,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import * as DocumentPicker from 'expo-document-picker';
import { Header, FormHeader, ScreenLayout, colors, spacing, typography } from '../components';
import { BusinessUserPicker } from '../components/BusinessUserPicker';
import { DatePickerField } from '../components/DatePickerField';
import { BulkContactMapperScreen } from '../components/BulkContactMapperScreen';
import { VisitOverrideEditor, VisitOverrideValues, cleanOverrideGroup } from '../components/VisitOverrideEditor';
import { useAppContext } from '../contexts/AppContext';
import { useLocale } from '../contexts/LocaleContext';
import { createTabPressHandler } from '../navigation/handleTabPress';
import { apiService, DataListWebhookFieldMapping, VisitCampaign } from '../services/api';
import { useThemedStyles } from '../hooks/useThemedStyles';

interface ContactSearchOption {
  contactId: string;
  name: string;
  phone?: string;
  address?: string;
}

interface SelectedContact {
  contactId: string;
  name: string;
}

const DEBOUNCE_MS = 300;
const CONTACT_PAGE_SIZE = 25;
const STEP_COUNT = 4;

const MUST_ASSIGN_ERROR = 'Campaign must have at least one assigned department or user before contacts can be attached';

interface PrefillContact {
  contactId: string;
  name: string;
}

export const VisitCampaignCreateScreen: React.FC = () => {
  const styles = useThemedStyles(getStyles);
  const { navigateTo, showNotification, routeParams, activeTab, setActiveTab } = useAppContext();
  const { t } = useLocale();
  const handleTabPress = createTabPressHandler(navigateTo, setActiveTab);

  const prefillContacts: PrefillContact[] | undefined = routeParams?.prefillContacts;

  const [step, setStep] = useState(0);

  // Step 1: basics
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [startDate, setStartDate] = useState<Date | null>(null);
  const [endDate, setEndDate] = useState<Date | null>(null);
  const [expectedDurationMinutes, setExpectedDurationMinutes] = useState('');
  const [dailyVisitTarget, setDailyVisitTarget] = useState('');
  // Campaign-level override of the service's default enforcement — full
  // field set via the shared VisitOverrideEditor. Every visit created from
  // this campaign inherits these unless a specific visit further overrides
  // them itself (VisitCreateScreen's own equivalent section).
  const [overrides, setOverrides] = useState<VisitOverrideValues>({});

  // Step 2: assignment
  const [assignedDepartmentIds, setAssignedDepartmentIds] = useState<string[]>([]);
  const [assignedUserIds, setAssignedUserIds] = useState<string[]>([]);
  const [showDepartmentPicker, setShowDepartmentPicker] = useState(false);
  const [showUserPicker, setShowUserPicker] = useState(false);
  const [assignmentWarning, setAssignmentWarning] = useState(false);
  const [isCreatingCampaign, setIsCreatingCampaign] = useState(false);

  // Campaign created at the end of step 2 (draft) — contacts attach to a real id.
  const [campaignId, setCampaignId] = useState<string | null>(null);
  const [campaign, setCampaign] = useState<VisitCampaign | null>(null);

  // Step 3: contact pool
  const [contactPoolTab, setContactPoolTab] = useState<'select' | 'upload'>('select');
  // Seeded once from the Contacts screen's "Create Visits" bulk action, if
  // present — stays editable (add/remove via the chips below) and only
  // actually attaches to the campaign once the user reaches this step and
  // presses "Attach selected" (attachSelectedContacts needs campaignId,
  // which doesn't exist yet on steps 0/1).
  const [selectedContacts, setSelectedContacts] = useState<SelectedContact[]>(
    () => prefillContacts?.map(c => ({ contactId: c.contactId, name: c.name })) || []
  );
  const [showContactPicker, setShowContactPicker] = useState(false);
  const [contactQuery, setContactQuery] = useState('');
  const [contactResults, setContactResults] = useState<ContactSearchOption[]>([]);
  const [isSearchingContacts, setIsSearchingContacts] = useState(false);
  const [isLoadingMoreContacts, setIsLoadingMoreContacts] = useState(false);
  // Same real pagination bug as VisitCreateScreen's single-contact picker —
  // a flat limit:25 with no offset/onEndReached meant any business with
  // more contacts than that could never reach the rest, regardless of
  // search query.
  const [contactHasMore, setContactHasMore] = useState(true);
  const [isAttachingContacts, setIsAttachingContacts] = useState(false);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const [pickedFile, setPickedFile] = useState<{ uri: string; name: string; mimeType: string } | null>(null);
  const [previewData, setPreviewData] = useState<any>(null);
  const [showMapper, setShowMapper] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);

  const [isActivating, setIsActivating] = useState(false);

  const mapContact = (c: any): ContactSearchOption => ({
    contactId: String(c._id),
    name: c.profile?.name || c.profile?.phone || '',
    phone: c.profile?.phone,
    address: c.profile?.location?.address,
  });

  const searchContacts = useCallback(async (text: string) => {
    setIsSearchingContacts(true);
    try {
      // hasLocationOrAddress: a contact with no coordinates/address/city/
      // country can never actually have a visit created for it (see
      // visit-target-location.util.ts) — filtering it out of the picker
      // itself avoids the confusing "why isn't my selection all here"
      // experience of only discovering this after attaching contacts.
      const res = await apiService.getParentContacts({ name: text || undefined, limit: CONTACT_PAGE_SIZE, offset: 0, hasLocationOrAddress: true });
      const raw: any[] = (res.data as any)?.contacts || (Array.isArray(res.data) ? res.data : []) || [];
      setContactResults(raw.map(mapContact));
      setContactHasMore(raw.length === CONTACT_PAGE_SIZE);
    } catch (err) {
      console.warn('[VisitCampaignCreate] searchContacts error:', err);
      setContactResults([]);
      setContactHasMore(false);
    } finally {
      setIsSearchingContacts(false);
    }
  }, []);

  const loadMoreContacts = useCallback(async () => {
    if (isLoadingMoreContacts || isSearchingContacts || !contactHasMore) return;
    setIsLoadingMoreContacts(true);
    try {
      const res = await apiService.getParentContacts({
        name: contactQuery || undefined,
        limit: CONTACT_PAGE_SIZE,
        offset: contactResults.length,
        hasLocationOrAddress: true,
      });
      const raw: any[] = (res.data as any)?.contacts || (Array.isArray(res.data) ? res.data : []) || [];
      setContactResults((prev) => [...prev, ...raw.map(mapContact)]);
      setContactHasMore(raw.length === CONTACT_PAGE_SIZE);
    } catch (err) {
      console.warn('[VisitCampaignCreate] loadMoreContacts error:', err);
    } finally {
      setIsLoadingMoreContacts(false);
    }
  }, [contactQuery, contactResults.length, contactHasMore, isLoadingMoreContacts, isSearchingContacts]);

  useEffect(() => {
    if (!showContactPicker) return;
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => searchContacts(contactQuery), contactQuery ? DEBOUNCE_MS : 0);
    return () => { if (debounceRef.current) clearTimeout(debounceRef.current); };
  }, [contactQuery, showContactPicker, searchContacts]);

  const toggleContact = (option: ContactSearchOption) => {
    setSelectedContacts((prev) => {
      if (prev.some((c) => c.contactId === option.contactId)) {
        return prev.filter((c) => c.contactId !== option.contactId);
      }
      return [...prev, { contactId: option.contactId, name: option.name }];
    });
  };

  const isContactSelected = (contactId: string) => selectedContacts.some((c) => c.contactId === contactId);

  const goBack = () => {
    if (step === 0) {
      navigateTo('visits-campaigns');
    } else {
      setStep((s) => s - 1);
    }
  };

  const validateBasics = (): boolean => {
    if (!name.trim()) {
      showNotification('warning', t('visitCampaigns.create.title'), t('visitCampaigns.create.errors.nameRequired'));
      return false;
    }
    if (!startDate || !endDate) {
      showNotification('warning', t('visitCampaigns.create.title'), t('visitCampaigns.create.errors.datesRequired'));
      return false;
    }
    return true;
  };

  const handleNextFromBasics = () => {
    if (!validateBasics()) return;
    setStep(1);
  };

  const handleNextFromAssignment = async () => {
    if (assignedDepartmentIds.length === 0 && assignedUserIds.length === 0) {
      setAssignmentWarning(true);
      return;
    }
    setAssignmentWarning(false);
    setIsCreatingCampaign(true);
    try {
      const payload: Record<string, any> = {
        name: name.trim(),
        startDate: startDate!.toISOString(),
        endDate: endDate!.toISOString(),
        assignedDepartmentIds,
        assignedUserIds,
      };
      if (description.trim()) payload.description = description.trim();
      if (expectedDurationMinutes.trim()) payload.expectedDurationMinutes = Number(expectedDurationMinutes) || undefined;
      if (dailyVisitTarget.trim()) payload.dailyVisitTarget = Number(dailyVisitTarget) || undefined;
      const locationOverride = cleanOverrideGroup(overrides.location);
      const photosOverride = cleanOverrideGroup(overrides.photos);
      const calendarOverride = cleanOverrideGroup(overrides.calendar);
      if (locationOverride) payload.locationOverride = locationOverride;
      if (photosOverride) payload.photosOverride = photosOverride;
      if (calendarOverride) payload.calendarOverride = calendarOverride;

      const res = await apiService.createVisitCampaign(payload);
      if (res.statusCode === 1000 && res.data) {
        setCampaignId(res.data._id);
        setCampaign(res.data);
        setStep(2);
      } else {
        showNotification('error', t('visitCampaigns.create.title'), res.errors?.[0]?.message || t('visits.errors.generic'));
      }
    } catch (err: any) {
      showNotification('error', t('visitCampaigns.create.title'), err?.message || t('visits.errors.generic'));
    } finally {
      setIsCreatingCampaign(false);
    }
  };

  const attachSelectedContacts = async () => {
    if (!campaignId || selectedContacts.length === 0) return;
    setIsAttachingContacts(true);
    try {
      const res = await apiService.attachVisitCampaignContacts(campaignId, selectedContacts.map((c) => c.contactId));
      if (res.statusCode === 1000) {
        const skipped = res.data?.skippedNoLocation?.length || 0;
        showNotification(
          'success',
          t('visitCampaigns.create.contactPoolSection'),
          t('visitCampaigns.create.contactsAttached', { count: res.data?.attached ?? selectedContacts.length })
        );
        if (skipped > 0) {
          showNotification('warning', t('visitCampaigns.create.contactPoolSection'), t('visitCampaigns.create.skippedNoLocation', { count: skipped }));
        }
        setSelectedContacts([]);
        const refreshed = await apiService.getVisitCampaign(campaignId);
        if (refreshed.statusCode === 1000 && refreshed.data) setCampaign(refreshed.data);
      } else {
        const message = res.errors?.[0]?.message || '';
        showNotification('error', t('visitCampaigns.create.contactPoolSection'), message.includes(MUST_ASSIGN_ERROR) ? t('visitCampaigns.create.errors.mustAssignFirst') : (message || t('visits.errors.generic')));
      }
    } catch (err: any) {
      showNotification('error', t('visitCampaigns.create.contactPoolSection'), err?.message || t('visits.errors.generic'));
    } finally {
      setIsAttachingContacts(false);
    }
  };

  const handlePickFile = async () => {
    try {
      const result = await DocumentPicker.getDocumentAsync({
        type: [
          'text/csv',
          'text/comma-separated-values',
          'application/vnd.ms-excel',
          'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        ],
        copyToCacheDirectory: true,
      });
      if (result.canceled) return;
      const file = result.assets[0];
      if (!file) return;

      setIsUploading(true);
      const mimeType = file.mimeType || 'text/csv';
      const response = await apiService.previewBulkContactUpload(file.uri, file.name, mimeType);
      if (response.statusCode === 1000 && response.data) {
        setPickedFile({ uri: file.uri, name: file.name, mimeType });
        setPreviewData(response.data);
        setShowMapper(true);
      } else {
        throw new Error(response.errors?.[0]?.message || t('visits.errors.generic'));
      }
    } catch (err: any) {
      showNotification('error', t('visitCampaigns.create.uploadFileTab'), err?.message || t('visits.errors.generic'));
    } finally {
      setIsUploading(false);
    }
  };

  const pollUploadJob = async (jobId: string) => {
    const POLL_INTERVAL_MS = 3000;
    const MAX_ATTEMPTS = 200;

    for (let attempt = 0; attempt < MAX_ATTEMPTS; attempt++) {
      await new Promise((resolve) => setTimeout(resolve, POLL_INTERVAL_MS));

      let statusResponse;
      try {
        statusResponse = await apiService.getBulkContactUploadStatus(jobId);
      } catch {
        continue;
      }
      if (statusResponse.statusCode !== 1000 || !statusResponse.data) continue;

      const { status, error: jobError } = statusResponse.data;
      setUploadProgress(Math.min(95, Math.round(((attempt + 1) / MAX_ATTEMPTS) * 100)));

      if (status === 'failed') {
        showNotification('error', t('visitCampaigns.create.uploadFileTab'), jobError || t('visits.errors.generic'));
        setIsUploading(false);
        return;
      }

      if (status === 'completed') {
        setUploadProgress(100);
        if (campaignId) {
          try {
            const attachRes = await apiService.attachVisitCampaignContactsFromJob(campaignId, jobId);
            if (attachRes.statusCode === 1000) {
              showNotification(
                'success',
                t('visitCampaigns.create.uploadFileTab'),
                t('visitCampaigns.create.contactsAttached', { count: attachRes.data?.attached ?? 0 })
              );
              const refreshed = await apiService.getVisitCampaign(campaignId);
              if (refreshed.statusCode === 1000 && refreshed.data) setCampaign(refreshed.data);
            } else {
              showNotification('error', t('visitCampaigns.create.uploadFileTab'), attachRes.errors?.[0]?.message || t('visits.errors.generic'));
            }
          } catch (err: any) {
            showNotification('error', t('visitCampaigns.create.uploadFileTab'), err?.message || t('visits.errors.generic'));
          }
        }
        setIsUploading(false);
        setPickedFile(null);
        setPreviewData(null);
        return;
      }
    }

    setIsUploading(false);
    showNotification('error', t('visitCampaigns.create.uploadFileTab'), t('visitCampaigns.create.uploadStillProcessing'));
  };

  const handleConfirmMapping = async (mapping: DataListWebhookFieldMapping[]) => {
    if (!pickedFile) return;
    setIsUploading(true);
    try {
      const res = await apiService.commitBulkContactUpload(pickedFile.uri, pickedFile.name, pickedFile.mimeType, mapping, 'whatsapp');
      if (res.statusCode === 1000 && res.data?.jobId) {
        setShowMapper(false);
        setUploadProgress(0);
        pollUploadJob(res.data.jobId);
      } else {
        throw new Error(res.errors?.[0]?.message || t('visits.errors.generic'));
      }
    } catch (err: any) {
      showNotification('error', t('visitCampaigns.create.uploadFileTab'), err?.message || t('visits.errors.generic'));
      setIsUploading(false);
    }
  };

  const handleActivate = async () => {
    if (!campaignId) return;
    setIsActivating(true);
    try {
      // Selecting contacts on the pool step doesn't attach them by itself —
      // that needs its own explicit "Attach selected" tap, which is easy to
      // miss entirely if the user goes straight from selecting to Review.
      // Auto-attach anything still pending here so activation never silently
      // produces a campaign with zero Visit records despite contacts having
      // been chosen.
      let effectiveContactCount = campaign?.contactCount ?? 0;
      if (selectedContacts.length > 0) {
        const attachRes = await apiService.attachVisitCampaignContacts(campaignId, selectedContacts.map((c) => c.contactId));
        if (attachRes.statusCode === 1000) {
          effectiveContactCount += attachRes.data?.attached ?? 0;
          setSelectedContacts([]);
          const refreshed = await apiService.getVisitCampaign(campaignId);
          if (refreshed.statusCode === 1000 && refreshed.data) setCampaign(refreshed.data);
        }
      }

      if (effectiveContactCount === 0) {
        showNotification('warning', t('visitCampaigns.create.reviewSection'), t('visitCampaigns.create.errors.noContactsBeforeActivate'));
        setIsActivating(false);
        return;
      }

      const res = await apiService.activateVisitCampaign(campaignId);
      if (res.statusCode === 1000) {
        navigateTo('visit-campaign-detail', undefined, { campaignId });
      } else {
        showNotification('error', t('visitCampaigns.create.reviewSection'), res.errors?.[0]?.message || t('visits.errors.generic'));
      }
    } catch (err: any) {
      showNotification('error', t('visitCampaigns.create.reviewSection'), err?.message || t('visits.errors.generic'));
    } finally {
      setIsActivating(false);
    }
  };

  const renderStepBasics = () => (
    <>
      <View style={styles.formCard}>
        <Text style={styles.sectionTitle}>{t('visitCampaigns.create.basicsSection')}</Text>
        <Text style={styles.formLabel}>{t('visitCampaigns.create.name')}</Text>
        <TextInput style={styles.input} value={name} onChangeText={setName} placeholder={t('visitCampaigns.create.namePlaceholder')} />
        <Text style={styles.formLabel}>{t('visitCampaigns.create.description')}</Text>
        <TextInput
          style={[styles.input, styles.multilineInput]}
          value={description}
          onChangeText={setDescription}
          placeholder={t('visitCampaigns.create.descriptionPlaceholder')}
          multiline
          numberOfLines={3}
        />
        <DatePickerField label={t('visitCampaigns.create.startDate')} value={startDate} onChange={setStartDate} />
        <DatePickerField label={t('visitCampaigns.create.endDate')} value={endDate} onChange={setEndDate} minimumDate={startDate || undefined} />
        <Text style={styles.formLabel}>{t('visitCampaigns.create.expectedDuration')}</Text>
        <TextInput
          style={styles.input}
          keyboardType="number-pad"
          value={expectedDurationMinutes}
          onChangeText={setExpectedDurationMinutes}
          placeholder={t('visitCampaigns.create.expectedDurationPlaceholder')}
        />
        <Text style={styles.formLabel}>{t('visitCampaigns.create.dailyVisitTarget')}</Text>
        <TextInput
          style={styles.input}
          keyboardType="number-pad"
          value={dailyVisitTarget}
          onChangeText={setDailyVisitTarget}
          placeholder={t('visitCampaigns.create.dailyVisitTargetPlaceholder')}
        />
      </View>

      {/* Campaign-level override of the service's default enforcement —
          full field set via the shared editor. Every visit created from
          this campaign inherits these unless a specific visit further
          overrides them via VisitCreateScreen's own equivalent section. */}
      <View style={styles.formCard}>
        <VisitOverrideEditor value={overrides} onChange={setOverrides} />
      </View>

      <TouchableOpacity style={styles.primaryButton} onPress={handleNextFromBasics}>
        <Text style={styles.primaryButtonText}>{t('visitCampaigns.create.next')}</Text>
      </TouchableOpacity>
    </>
  );

  const renderStepAssignment = () => (
    <>
      <View style={styles.formCard}>
        <Text style={styles.sectionTitle}>{t('visitCampaigns.create.assignmentSection')}</Text>
        <Text style={styles.formLabel}>{t('visitCampaigns.create.assignedDepartments')}</Text>
        <TouchableOpacity style={styles.pickerButton} onPress={() => setShowDepartmentPicker(true)}>
          <Text style={styles.pickerButtonText}>
            {assignedDepartmentIds.length > 0
              ? t('visitCampaigns.create.selectedCount', { count: assignedDepartmentIds.length })
              : t('visitCampaigns.create.selectDepartments')}
          </Text>
        </TouchableOpacity>
        <Text style={styles.formLabel}>{t('visitCampaigns.create.assignedUsers')}</Text>
        <TouchableOpacity style={styles.pickerButton} onPress={() => setShowUserPicker(true)}>
          <Text style={styles.pickerButtonText}>
            {assignedUserIds.length > 0
              ? t('visitCampaigns.create.selectedCount', { count: assignedUserIds.length })
              : t('visitCampaigns.create.selectUsers')}
          </Text>
        </TouchableOpacity>
        {assignmentWarning && (
          <View style={styles.errorBox}>
            <Ionicons name="warning-outline" size={16} color={colors.status.error as string} />
            <Text style={styles.errorBoxText}>{t('visitCampaigns.create.errors.assignmentRequired')}</Text>
          </View>
        )}
      </View>
      <TouchableOpacity
        style={[styles.primaryButton, isCreatingCampaign && styles.disabledButton]}
        onPress={handleNextFromAssignment}
        disabled={isCreatingCampaign}
      >
        {isCreatingCampaign ? (
          <ActivityIndicator color={colors.white as string} />
        ) : (
          <Text style={styles.primaryButtonText}>{t('visitCampaigns.create.next')}</Text>
        )}
      </TouchableOpacity>

      <BusinessUserPicker
        visible={showDepartmentPicker}
        onClose={() => setShowDepartmentPicker(false)}
        onConfirm={(ids) => { setAssignedDepartmentIds(ids); setShowDepartmentPicker(false); }}
        mode="multiple"
        source="departments"
        selectedUserIds={assignedDepartmentIds}
        title={t('visitCampaigns.create.selectDepartments')}
      />
      <BusinessUserPicker
        visible={showUserPicker}
        onClose={() => setShowUserPicker(false)}
        onConfirm={(ids) => { setAssignedUserIds(ids); setShowUserPicker(false); }}
        mode="multiple"
        source="users"
        selectedUserIds={assignedUserIds}
        title={t('visitCampaigns.create.selectUsers')}
      />
    </>
  );

  const renderStepContactPool = () => (
    <>
      <View style={styles.formCard}>
        <Text style={styles.sectionTitle}>{t('visitCampaigns.create.contactPoolSection')}</Text>
        <Text style={styles.metaText}>{t('visitCampaigns.create.contactCountSoFar', { count: campaign?.contactCount ?? 0 })}</Text>

        <View style={styles.segmentedRow}>
          <TouchableOpacity
            style={[styles.segmentButton, contactPoolTab === 'select' && styles.segmentButtonActive]}
            onPress={() => setContactPoolTab('select')}
          >
            <Text style={[styles.segmentButtonText, contactPoolTab === 'select' && styles.segmentButtonTextActive]}>
              {t('visitCampaigns.create.selectContactsTab')}
            </Text>
          </TouchableOpacity>
          <TouchableOpacity
            style={[styles.segmentButton, contactPoolTab === 'upload' && styles.segmentButtonActive]}
            onPress={() => setContactPoolTab('upload')}
          >
            <Text style={[styles.segmentButtonText, contactPoolTab === 'upload' && styles.segmentButtonTextActive]}>
              {t('visitCampaigns.create.uploadFileTab')}
            </Text>
          </TouchableOpacity>
        </View>

        {contactPoolTab === 'select' ? (
          <>
            <TouchableOpacity style={styles.pickerButton} onPress={() => { setContactQuery(''); setShowContactPicker(true); }}>
              <Text style={styles.pickerButtonText}>{t('visitCampaigns.create.searchAndSelectContacts')}</Text>
            </TouchableOpacity>
            {selectedContacts.length > 0 && (
              <View style={styles.selectedChipsWrap}>
                {selectedContacts.map((c) => (
                  <View key={c.contactId} style={styles.selectedChip}>
                    <Text style={styles.selectedChipText} numberOfLines={1}>{c.name}</Text>
                    <TouchableOpacity onPress={() => toggleContact({ contactId: c.contactId, name: c.name })}>
                      <Ionicons name="close-circle" size={16} color={colors.text.secondary as string} />
                    </TouchableOpacity>
                  </View>
                ))}
              </View>
            )}
            <TouchableOpacity
              style={[styles.secondaryButton, (selectedContacts.length === 0 || isAttachingContacts) && styles.disabledButton]}
              onPress={attachSelectedContacts}
              disabled={selectedContacts.length === 0 || isAttachingContacts}
            >
              {isAttachingContacts ? (
                <ActivityIndicator color={colors.primary as string} />
              ) : (
                <Text style={styles.secondaryButtonText}>{t('visitCampaigns.create.attachSelected', { count: selectedContacts.length })}</Text>
              )}
            </TouchableOpacity>
          </>
        ) : (
          <>
            <TouchableOpacity style={[styles.pickerButton, isUploading && styles.disabledButton]} onPress={handlePickFile} disabled={isUploading}>
              <Text style={styles.pickerButtonText}>{t('visitCampaigns.create.chooseFile')}</Text>
            </TouchableOpacity>
            {isUploading && (
              <View style={styles.uploadProgressBox}>
                <ActivityIndicator color={colors.primary as string} />
                <Text style={styles.metaText}>{t('visitCampaigns.create.uploadingProgress', { percent: uploadProgress })}</Text>
              </View>
            )}
          </>
        )}
      </View>

      <View style={styles.stepNavRow}>
        <TouchableOpacity style={styles.secondaryButton} onPress={goBack}>
          <Text style={styles.secondaryButtonText}>{t('visitCampaigns.create.back')}</Text>
        </TouchableOpacity>
        <TouchableOpacity style={styles.primaryButtonFlex} onPress={() => setStep(3)}>
          <Text style={styles.primaryButtonText}>{t('visitCampaigns.create.next')}</Text>
        </TouchableOpacity>
      </View>

      <RNModal visible={showContactPicker} transparent animationType="fade" onRequestClose={() => setShowContactPicker(false)}>
        <TouchableOpacity style={styles.overlay} activeOpacity={1} onPress={() => setShowContactPicker(false)}>
          <TouchableOpacity activeOpacity={1} style={styles.pickerCard} onPress={() => {}}>
            <Text style={styles.pickerTitle}>{t('visitCampaigns.create.searchAndSelectContacts')}</Text>
            <TextInput
              style={styles.searchInput}
              placeholder={t('visits.create.searchContacts')}
              value={contactQuery}
              onChangeText={setContactQuery}
              autoCapitalize="none"
            />
            {isSearchingContacts ? (
              <View style={styles.loadingBox}>
                <ActivityIndicator color={colors.primary as string} />
              </View>
            ) : (
              <FlatList
                data={contactResults}
                keyExtractor={(c) => c.contactId}
                style={styles.pickerList}
                keyboardShouldPersistTaps="handled"
                onEndReachedThreshold={0.4}
                onEndReached={loadMoreContacts}
                ListEmptyComponent={<Text style={styles.emptyText}>{t('visits.create.noContactsFound')}</Text>}
                ListFooterComponent={isLoadingMoreContacts ? (
                  <View style={styles.loadingBox}>
                    <ActivityIndicator color={colors.primary as string} size="small" />
                  </View>
                ) : null}
                renderItem={({ item }) => {
                  const selected = isContactSelected(item.contactId);
                  return (
                    <TouchableOpacity style={styles.contactOption} onPress={() => toggleContact(item)}>
                      <Ionicons
                        name={selected ? 'checkbox' : 'square-outline'}
                        size={20}
                        color={selected ? (colors.primary as string) : (colors.text.secondary as string)}
                      />
                      <View style={styles.contactOptionTextWrap}>
                        <Text style={styles.contactOptionName}>{item.name}</Text>
                        {item.phone ? <Text style={styles.metaText}>{item.phone}</Text> : null}
                      </View>
                    </TouchableOpacity>
                  );
                }}
              />
            )}
            <TouchableOpacity style={styles.primaryButton} onPress={() => setShowContactPicker(false)}>
              <Text style={styles.primaryButtonText}>{t('visitCampaigns.create.done')}</Text>
            </TouchableOpacity>
          </TouchableOpacity>
        </TouchableOpacity>
      </RNModal>

      {previewData && (
        <BulkContactMapperScreen
          visible={showMapper}
          onClose={() => setShowMapper(false)}
          headers={previewData.headers}
          totalRows={previewData.totalRows}
          targetFields={previewData.targetFields}
          suggestedMapping={previewData.suggestedMapping}
          fromRememberedMapping={previewData.fromRememberedMapping}
          committing={isUploading}
          onConfirm={handleConfirmMapping}
        />
      )}
    </>
  );

  const renderStepReview = () => (
    <>
      <View style={styles.formCard}>
        <Text style={styles.sectionTitle}>{t('visitCampaigns.create.reviewSection')}</Text>
        <View style={styles.reviewRow}>
          <Text style={styles.reviewLabel}>{t('visitCampaigns.create.name')}</Text>
          <Text style={styles.reviewValue}>{name}</Text>
        </View>
        <View style={styles.reviewRow}>
          <Text style={styles.reviewLabel}>{t('visitCampaigns.create.startDate')}</Text>
          <Text style={styles.reviewValue}>{startDate ? startDate.toLocaleDateString() : '—'}</Text>
        </View>
        <View style={styles.reviewRow}>
          <Text style={styles.reviewLabel}>{t('visitCampaigns.create.endDate')}</Text>
          <Text style={styles.reviewValue}>{endDate ? endDate.toLocaleDateString() : '—'}</Text>
        </View>
        <View style={styles.reviewRow}>
          <Text style={styles.reviewLabel}>{t('visitCampaigns.create.assignedDepartments')}</Text>
          <Text style={styles.reviewValue}>{assignedDepartmentIds.length}</Text>
        </View>
        <View style={styles.reviewRow}>
          <Text style={styles.reviewLabel}>{t('visitCampaigns.create.assignedUsers')}</Text>
          <Text style={styles.reviewValue}>{assignedUserIds.length}</Text>
        </View>
        <View style={styles.reviewRow}>
          <Text style={styles.reviewLabel}>
            {t('visitCampaigns.list.contactCount', { count: (campaign?.contactCount ?? 0) + selectedContacts.length })}
          </Text>
        </View>
        {selectedContacts.length > 0 && (
          <Text style={styles.metaText}>{t('visitCampaigns.create.pendingAttachNote', { count: selectedContacts.length })}</Text>
        )}
      </View>

      <TouchableOpacity
        style={[styles.primaryButton, isActivating && styles.disabledButton]}
        onPress={handleActivate}
        disabled={isActivating}
      >
        {isActivating ? (
          <ActivityIndicator color={colors.white as string} />
        ) : (
          <Text style={styles.primaryButtonText}>{t('visitCampaigns.create.activateCampaign')}</Text>
        )}
      </TouchableOpacity>
      <TouchableOpacity
        style={styles.secondaryButton}
        onPress={() => campaignId && navigateTo('visit-campaign-detail', undefined, { campaignId })}
      >
        <Text style={styles.secondaryButtonText}>{t('visitCampaigns.create.saveAsDraft')}</Text>
      </TouchableOpacity>
    </>
  );

  const steps = [renderStepBasics, renderStepAssignment, renderStepContactPool, renderStepReview];

  return (
    <ScreenLayout
      header={
        <Header
          showNotifications={true}
          showMessages={true}
          onNotificationPress={() => navigateTo('notifications')}
          onMessagePress={() => navigateTo('chat')}
          showNavigation={Platform.OS === 'web'}
          activeTab={activeTab}
          onTabPress={handleTabPress}
        />
      }
      showBottomTabs={Platform.OS !== 'web'}
      onLeftPaneClose={() => {}}
      leftPaneContent="settings"
      onSettingsItemPress={async (itemId) => {
        const { setPendingSettingsItem } = await import('../utils/tempStorage');
        await setPendingSettingsItem(itemId);
        navigateTo('settings');
      }}
      showRightPane={false}
      formHeader={<FormHeader title={t('visitCampaigns.create.title')} onBack={goBack} />}
    >
      <View style={styles.screen}>
      <View style={styles.stepIndicatorRow}>
        {Array.from({ length: STEP_COUNT }).map((_, i) => (
          <View key={i} style={[styles.stepDot, i === step && styles.stepDotActive, i < step && styles.stepDotDone]} />
        ))}
      </View>
      <ScrollView contentContainerStyle={styles.content}>
        {steps[step]()}
      </ScrollView>
      </View>
    </ScreenLayout>
  );
};

const getStyles = () => StyleSheet.create({
  screen: { flex: 1 },
  content: { padding: spacing.lg, paddingBottom: spacing.xl * 2 },

  stepIndicatorRow: { flexDirection: 'row', justifyContent: 'center', gap: spacing.xs, paddingTop: spacing.sm },
  stepDot: { width: 8, height: 8, borderRadius: 4, backgroundColor: colors.border as string },
  stepDotActive: { backgroundColor: colors.primary as string, width: 20 },
  stepDotDone: { backgroundColor: colors.status.success as string },

  formCard: {
    backgroundColor: colors.background.surface as string,
    borderRadius: 12,
    padding: spacing.lg,
    borderWidth: 1,
    borderColor: colors.border as string,
    marginBottom: spacing.lg,
  },
  sectionTitle: {
    fontSize: typography.fontSize.xl,
    fontFamily: typography.fontFamily.semibold,
    color: colors.text.primary as string,
    marginBottom: spacing.sm,
  },
  overrideToggleRow: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between' },
  switchRow: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between' },
  formLabel: {
    fontSize: typography.fontSize.base,
    fontFamily: typography.fontFamily.semibold,
    color: colors.text.primary as string,
    marginBottom: spacing.xs,
    marginTop: spacing.sm,
  },
  input: {
    borderWidth: 1,
    borderColor: colors.border as string,
    borderRadius: 8,
    paddingHorizontal: spacing.md,
    paddingVertical: spacing.sm,
    fontSize: typography.fontSize.base,
    fontFamily: typography.fontFamily.regular,
    color: colors.text.primary as string,
    backgroundColor: colors.background.surface as string,
    marginBottom: spacing.sm,
  },
  multilineInput: { minHeight: 80, textAlignVertical: 'top' },

  pickerButton: {
    borderWidth: 1,
    borderColor: colors.border as string,
    borderRadius: 8,
    paddingVertical: spacing.sm,
    paddingHorizontal: spacing.md,
    alignItems: 'center',
    marginBottom: spacing.sm,
  },
  pickerButtonText: { fontSize: typography.fontSize.base, fontFamily: typography.fontFamily.semibold, color: colors.primary as string },

  errorBox: { flexDirection: 'row', alignItems: 'center', gap: spacing.xs, marginTop: spacing.xs },
  errorBoxText: { fontSize: typography.fontSize.sm, fontFamily: typography.fontFamily.regular, color: colors.status.error as string, flex: 1 },

  segmentedRow: { flexDirection: 'row', flexWrap: 'wrap', gap: spacing.xs, marginBottom: spacing.sm },
  segmentButton: {
    borderWidth: 1,
    borderColor: colors.border as string,
    borderRadius: 8,
    paddingVertical: spacing.xs,
    paddingHorizontal: spacing.sm,
  },
  segmentButtonActive: { backgroundColor: colors.primary as string, borderColor: colors.primary as string },
  segmentButtonText: { fontSize: typography.fontSize.sm, fontFamily: typography.fontFamily.regular, color: colors.text.primary as string },
  segmentButtonTextActive: { color: colors.white as string, fontFamily: typography.fontFamily.semibold },

  metaText: { fontSize: typography.fontSize.sm, fontFamily: typography.fontFamily.regular, color: colors.text.secondary as string, marginBottom: spacing.sm },

  selectedChipsWrap: { flexDirection: 'row', flexWrap: 'wrap', gap: spacing.xs, marginBottom: spacing.sm },
  selectedChip: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
    backgroundColor: colors.background.secondary as string,
    borderRadius: 14,
    paddingVertical: 4,
    paddingHorizontal: spacing.sm,
    maxWidth: 160,
  },
  selectedChipText: { fontSize: typography.fontSize.sm, fontFamily: typography.fontFamily.regular, color: colors.text.primary as string, flexShrink: 1 },

  uploadProgressBox: { flexDirection: 'row', alignItems: 'center', gap: spacing.sm, marginTop: spacing.sm },

  primaryButton: { backgroundColor: colors.primary as string, borderRadius: 12, paddingVertical: spacing.md, alignItems: 'center', marginTop: spacing.md },
  primaryButtonFlex: { flex: 1, backgroundColor: colors.primary as string, borderRadius: 12, paddingVertical: spacing.md, alignItems: 'center' },
  primaryButtonText: { color: colors.white as string, fontSize: typography.fontSize.base, fontFamily: typography.fontFamily.semibold },
  secondaryButton: {
    borderWidth: 1,
    borderColor: colors.primary as string,
    borderRadius: 12,
    paddingVertical: spacing.md,
    alignItems: 'center',
    marginTop: spacing.md,
  },
  secondaryButtonText: { color: colors.primary as string, fontSize: typography.fontSize.base, fontFamily: typography.fontFamily.semibold },
  disabledButton: { opacity: 0.6 },

  stepNavRow: { flexDirection: 'row', gap: spacing.sm },

  reviewRow: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', paddingVertical: spacing.xs, borderBottomWidth: 1, borderBottomColor: colors.border as string },
  reviewLabel: { fontSize: typography.fontSize.sm, fontFamily: typography.fontFamily.regular, color: colors.text.secondary as string },
  reviewValue: { fontSize: typography.fontSize.base, fontFamily: typography.fontFamily.semibold, color: colors.text.primary as string },

  overlay: { flex: 1, backgroundColor: 'rgba(0,0,0,0.4)', alignItems: 'center', justifyContent: 'center' },
  pickerCard: {
    width: '90%',
    maxWidth: 380,
    maxHeight: '80%',
    backgroundColor: colors.background.surface as string,
    borderRadius: 14,
    padding: spacing.lg,
    gap: spacing.sm,
  },
  pickerTitle: { fontSize: typography.fontSize.xl, fontFamily: typography.fontFamily.semibold, color: colors.text.primary as string },
  searchInput: {
    height: 40,
    borderRadius: 8,
    paddingHorizontal: spacing.sm,
    backgroundColor: colors.background.secondary as string,
    color: colors.text.primary as string,
    fontSize: typography.fontSize.sm,
  },
  loadingBox: { paddingVertical: spacing.lg, alignItems: 'center' },
  pickerList: { maxHeight: 320 },
  emptyText: { fontSize: typography.fontSize.sm, color: colors.text.secondary as string, textAlign: 'center', paddingVertical: spacing.lg },
  contactOption: { flexDirection: 'row', alignItems: 'center', gap: spacing.sm, paddingVertical: spacing.sm, borderBottomWidth: 1, borderBottomColor: colors.border as string },
  contactOptionTextWrap: { flex: 1 },
  contactOptionName: { fontSize: typography.fontSize.base, fontFamily: typography.fontFamily.semibold, color: colors.text.primary as string },
});
